import React, { useEffect, useMemo, useState } from 'react';
import { getAuth } from 'firebase/auth';
import {
  addDoc,
  collection,
  collectionGroup,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  where,
} from 'firebase/firestore';
import { db } from '../../firebase';
import accessoryCatalog from '../../config/accessories';
import {
  colorOptions,
  finishOptions,
  flattenDesignCatalog,
  glassOptions,
  productCategories,
} from '../../config/productCatalog';
import { getWindowBasePrice } from '../../config/windowPricing';
import html2pdf from 'html2pdf.js';

const emptyItem = { name: '', description: '', quantity: 1, price: '' };
const statusOptions = [
  { value: 'pendiente', label: 'Pendiente' },
  { value: 'depositado', label: 'Depósito recibido' },
  { value: 'produccion', label: 'En producción' },
  { value: 'paid', label: 'Pagado' },
];
const EMAIL_ENDPOINT = 'https://sendclientemail-xjpm53xzha-uc.a.run.app/sendClientEmail';

const buildInvoiceHtml = (invoice = {}) => {
  const issueDate = new Date(invoice.createdAt?.toDate?.() || Date.now()).toLocaleDateString('es-PR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
  const itemsHtml = (invoice.items || [])
    .map((item) => {
      const quantity = Number(item.quantity || 1);
      const price = Number(item.price || 0);
      return `
        <tr>
          <td style="padding: 8px; border: 1px solid #e5e7eb;">${item.name || '-'}</td>
          <td style="padding: 8px; border: 1px solid #e5e7eb;">${item.description || '-'}</td>
          <td style="padding: 8px; border: 1px solid #e5e7eb; text-align:right;">${quantity}</td>
          <td style="padding: 8px; border: 1px solid #e5e7eb; text-align:right;">$${price.toFixed(2)}</td>
          <td style="padding: 8px; border: 1px solid #e5e7eb; text-align:right;">$${(price * quantity).toFixed(
            2
          )}</td>
        </tr>`;
    })
    .join('');
  const payments = (invoice.payments || []).reduce((sum, p) => sum + Number(p.amount || 0), 0);
  const resumenHtml = `
    <tr><td colspan="4" style="padding:8px; text-align:right;">Total</td><td style="padding:8px; text-align:right;">$${Number(
      invoice.total || 0
    ).toFixed(2)}</td></tr>
    <tr><td colspan="4" style="padding:8px; text-align:right;">Depósito</td><td style="padding:8px; text-align:right;">$${Number(
      invoice.deposit || 0
    ).toFixed(2)}</td></tr>
    <tr><td colspan="4" style="padding:8px; text-align:right;">Descuento</td><td style="padding:8px; text-align:right;">$${Number(
      invoice.discount || 0
    ).toFixed(2)}</td></tr>
    <tr><td colspan="4" style="padding:8px; text-align:right;">Abonos</td><td style="padding:8px; text-align:right;">$${payments.toFixed(
      2
    )}</td></tr>
    <tr><td colspan="4" style="padding:8px; text-align:right;font-weight:bold;">Saldo</td><td style="padding:8px; text-align:right;font-weight:bold;">$${Number(
      invoice.remaining || Number(invoice.total || 0) - Number(invoice.deposit || 0) - Number(invoice.discount || 0) - payments
    ).toFixed(2)}</td></tr>
  `;

  return `
    <div style="font-family: 'Helvetica Neue', Arial, sans-serif; padding: 16px; color: #111827;">
      <div style="text-align:center; border-bottom: 2px solid #059669; padding-bottom: 12px; margin-bottom: 16px;">
        <h2 style="margin: 0; color: #047857;">Puerto Rico Windows & Doors</h2>
        <p style="margin: 4px 0 0; font-size: 13px;">Factura generada por ${invoice.sellerName || 'Equipo de ventas'}</p>
      </div>
      <p><strong>Fecha:</strong> ${issueDate}</p>
      <p><strong>Cliente:</strong> ${invoice.clientName || 'N/A'}</p>
      <p><strong>Email:</strong> ${invoice.clientEmail || 'N/A'}</p>
      <p><strong>ID:</strong> ${invoice.id || '—'}</p>
      <table style="width:100%; border-collapse:collapse; margin-top:12px; font-size:13px;">
        <thead>
          <tr style="background:#ecfdf5; color:#065f46;">
            <th style="padding:8px; border:1px solid #e5e7eb;">Servicio</th>
            <th style="padding:8px; border:1px solid #e5e7eb;">Descripción</th>
            <th style="padding:8px; border:1px solid #e5e7eb;">Cantidad</th>
            <th style="padding:8px; border:1px solid #e5e7eb;">Precio</th>
            <th style="padding:8px; border:1px solid #e5e7eb;">Total</th>
          </tr>
        </thead>
        <tbody>
          ${itemsHtml || '<tr><td colspan="5" style="padding:8px; text-align:center;">Sin partidas</td></tr>'}
          ${resumenHtml}
        </tbody>
      </table>
      <p style="margin-top:16px;"><strong>Notas:</strong> ${invoice.notes || '—'}</p>
      <p><strong>Estado:</strong> ${(invoice.status || 'pendiente').toUpperCase()}</p>
    </div>
  `;
};

const generateInvoicePdfBlob = (invoice) =>
  html2pdf()
    .from(buildInvoiceHtml(invoice))
    .set({
      margin: 0.5,
      filename: `Factura-${invoice.id || 'PRWD'}.pdf`,
      html2canvas: { scale: 2 },
      jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' },
    })
    .outputPdf('blob');

const downloadInvoicePdf = (invoice) =>
  html2pdf()
    .from(buildInvoiceHtml(invoice))
    .set({
      margin: 0.5,
      filename: `Factura-${invoice.id || 'PRWD'}.pdf`,
      html2canvas: { scale: 2 },
      jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' },
    })
    .save();

const blobToBase64 = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      try {
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

const sendInvoiceEmailToClient = async (invoice, { silent = false } = {}) => {
  if (!invoice?.clientEmail) {
    if (!silent) alert('Este cliente no tiene email. Actualiza el perfil antes de enviar la factura.');
    return;
  }
  try {
    const blob = await generateInvoicePdfBlob(invoice);
    const base64Pdf = await blobToBase64(blob);
    const response = await fetch(EMAIL_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        to: invoice.clientEmail,
        subject: `Factura Puerto Rico Windows & Doors · ${invoice.id || ''}`,
        text: `Adjuntamos la factura solicitada. Saldo pendiente: $${Number(invoice.remaining || 0).toFixed(2)}.`,
        html: `<p>Adjuntamos la factura generada por ${invoice.sellerName || 'nuestro equipo'}.</p><p>Saldo pendiente: <strong>$${Number(
          invoice.remaining || 0
        ).toFixed(2)}</strong></p>`,
        attachments: [
          {
            content: base64Pdf,
            filename: `Factura-${invoice.id || 'PRWD'}.pdf`,
            type: 'application/pdf',
            disposition: 'attachment',
          },
        ],
      }),
    });
    if (!response.ok) {
      const text = await response.text();
      throw new Error(text || 'No pudimos enviar el correo.');
    }
    if (!silent) {
      alert('Factura enviada al cliente por email.');
    }
  } catch (err) {
    console.error('❌ Error enviando factura:', err);
    if (!silent) alert(err.message || 'No pudimos enviar el correo.');
  }
};

const SellerInvoices = () => {
  const auth = getAuth();
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [builderOpen, setBuilderOpen] = useState(false);
  const [builderError, setBuilderError] = useState('');
  const [savingInvoice, setSavingInvoice] = useState(false);
  const [itemList, setItemList] = useState([emptyItem]);
  const [deposit, setDeposit] = useState('');
  const [discount, setDiscount] = useState('');
  const [notes, setNotes] = useState('');
  const [status, setStatus] = useState('pendiente');
  const [emailSendingId, setEmailSendingId] = useState('');
  const [pdfGeneratingId, setPdfGeneratingId] = useState('');
  const designCatalog = useMemo(() => flattenDesignCatalog(), []);
  const accessoryOptions = accessoryCatalog;
  const defaultCategory = productCategories[0]?.id || 'ventanas';
  const defaultProduct = useMemo(
    () =>
      designCatalog.find((design) => design.categoryId === defaultCategory)?.id ||
      designCatalog[0]?.id ||
      '',
    [designCatalog]
  );
  const [clientForm, setClientForm] = useState({
    clientId: '',
    name: '',
    email: '',
    phone: '',
    projectLocation: '',
  });
  const [catalogBuilder, setCatalogBuilder] = useState(() => ({
    categoryId: defaultCategory,
    productId: defaultProduct,
    width: '',
    height: '',
    series: 'lama3',
    quantity: 1,
    color: colorOptions[0]?.id || '',
    glass: glassOptions[0]?.id || '',
    finish: finishOptions[0]?.id || '',
  }));
  const [addonSelection, setAddonSelection] = useState({});

  useEffect(() => {
    const fetchInvoices = async () => {
      const uid = auth.currentUser?.uid || null;
      const email = auth.currentUser?.email?.toLowerCase();
      if (!uid && !email) return;
      setLoading(true);
      setError(null);
      try {
        const invoicesQueries = [];
        if (uid) {
          invoicesQueries.push(
            query(collectionGroup(db, 'invoices'), where('sellerId', '==', uid))
          );
        }
        if (email) {
          invoicesQueries.push(
            query(collectionGroup(db, 'invoices'), where('sellerEmail', '==', email))
          );
        }
        const snapshots = await Promise.all(invoicesQueries.map((q) => getDocs(q)));
        const map = new Map();
        snapshots.forEach((snap) => {
          snap.forEach((docSnap) => {
            map.set(docSnap.ref.path, {
              id: docSnap.id,
              refPath: docSnap.ref.path,
              ...docSnap.data(),
            });
          });
        });
        const data = Array.from(map.values());
        data.sort(
          (a, b) =>
            (b.updatedAt?.toMillis?.() || b.updatedAt?.seconds || 0) -
            (a.updatedAt?.toMillis?.() || a.updatedAt?.seconds || 0)
        );
        setInvoices(data);
      } catch (err) {
        console.error('❌ Error leyendo facturas del vendedor:', err);
        setError('No pudimos cargar las facturas.');
      } finally {
        setLoading(false);
      }
    };

    fetchInvoices();
  }, [auth]);

  useEffect(() => {
    const fetchClients = async () => {
      const uid = auth.currentUser?.uid || null;
      const email = auth.currentUser?.email?.toLowerCase();
      if (!uid && !email) return;
      try {
        const snap = await getDocs(collection(db, 'clients'));
        const allowed = snap.docs
          .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
          .filter((client) => {
            const ownerUid = client.ownerUid || client.createdByUid || null;
            const assignedUid =
              client.assignedSellerUid || client.assignedToUid || client.salesRepUid || null;
            const assignedEmail =
              (client.assignedSellerEmail || client.assignedToEmail || '').toLowerCase();
            return (
              ownerUid === uid ||
              assignedUid === uid ||
              (assignedEmail && email && assignedEmail === email)
            );
          });
        allowed.sort((a, b) => (a.businessName || '').localeCompare(b.businessName || ''));
        setClients(allowed);
      } catch (err) {
        console.warn('No se pudieron cargar clientes para facturas del vendedor:', err);
      }
    };
    fetchClients();
  }, [auth]);

  const resetBuilder = () => {
    setClientForm({
      clientId: '',
      name: '',
      email: '',
      phone: '',
      projectLocation: '',
    });
    setItemList([emptyItem]);
    setDeposit('');
    setDiscount('');
    setNotes('');
    setStatus('pendiente');
    setBuilderError('');
    setCatalogBuilder({
      categoryId: defaultCategory,
      productId:
        designCatalog.find((design) => design.categoryId === defaultCategory)?.id ||
        designCatalog[0]?.id ||
        '',
      width: '',
      height: '',
      series: 'lama3',
      quantity: 1,
      color: colorOptions[0]?.id || '',
      glass: glassOptions[0]?.id || '',
      finish: finishOptions[0]?.id || '',
    });
    setAddonSelection({});
  };

  const handleSelectClient = (clientId) => {
    setClientForm((prev) => ({ ...prev, clientId }));
    if (!clientId) return;
    const client = clients.find((c) => c.id === clientId);
    if (client) {
      setClientForm({
        clientId,
        name: client.businessName || client.name || '',
        email: client.email || '',
        phone: client.phone || '',
        projectLocation: client.projectLocation || client.address || '',
      });
    }
  };

  const handleItemChange = (index, field, value) => {
    const updated = [...itemList];
    updated[index][field] =
      field === 'quantity' || field === 'price' ? value.replace(',', '.') : value;
    setItemList(updated);
  };

  const addItem = () => setItemList((prev) => [...prev, { ...emptyItem }]);
  const removeItem = (index) =>
    setItemList((prev) => prev.filter((_, i) => (prev.length === 1 ? true : i !== index)));

  const preparedItems = useMemo(() => {
    return itemList
      .map((item) => ({
        name: item.name?.trim(),
        description: item.description?.trim() || '',
        quantity: Number(item.quantity) || 1,
        price: Number(item.price) || 0,
      }))
      .filter((item) => item.name);
  }, [itemList]);

  const totals = useMemo(() => {
    const subtotal = preparedItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
    const depositNumber = Number(deposit) || 0;
    const discountNumber = Number(discount) || 0;
    const remaining = Math.max(subtotal - depositNumber - discountNumber, 0);
    return { subtotal, depositNumber, discountNumber, remaining };
  }, [preparedItems, deposit, discount]);

  const ensureClientRecord = async () => {
    let clientId = clientForm.clientId;
    if (clientId) return clientId;

    const name = clientForm.name.trim();
    const email = clientForm.email.trim().toLowerCase();
    if (!name || !email) {
      throw new Error('Debes indicar nombre y correo del cliente.');
    }

    const seller = auth.currentUser;
    const payload = {
      businessName: name,
      name,
      email,
      phone: clientForm.phone.trim() || '',
      address: clientForm.projectLocation.trim() || '',
      projectLocation: clientForm.projectLocation.trim() || '',
      ownerUid: seller?.uid || null,
      ownerEmail: seller?.email || null,
      assignedSellerUid: seller?.uid || null,
      assignedSellerEmail: seller?.email?.toLowerCase() || null,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      source: 'seller-panel',
    };

    const docRef = await addDoc(collection(db, 'clients'), payload);
    setClients((prev) => [...prev, { id: docRef.id, ...payload }]);
    return docRef.id;
  };

  const handleSubmitInvoice = async (e) => {
    e.preventDefault();
    setBuilderError('');
    if (!preparedItems.length) {
      setBuilderError('Añade al menos una línea (producto o servicio).');
      return;
    }
    if (!clientForm.name.trim() || !clientForm.email.trim()) {
      setBuilderError('Nombre y correo del cliente son obligatorios.');
      return;
    }
    const seller = auth.currentUser;
    if (!seller?.email) {
      setBuilderError('Tu sesión caducó. Ingresa nuevamente.');
      return;
    }
    setSavingInvoice(true);
    try {
      const clientId = await ensureClientRecord();
      const payload = {
        clientId,
        clientName: clientForm.name.trim(),
        clientEmail: clientForm.email.trim().toLowerCase(),
        clientPhone: clientForm.phone.trim() || '',
        projectLocation: clientForm.projectLocation.trim() || '',
        items: preparedItems,
        total: totals.subtotal,
        deposit: totals.depositNumber,
        discount: totals.discountNumber,
        payments: [],
        remaining: totals.remaining,
        status,
        notes: notes.trim(),
        sellerId: seller.uid,
        sellerEmail: seller.email.toLowerCase(),
        sellerName: seller.displayName || clientForm.name.trim(),
        createdBy: {
          uid: seller.uid,
          email: seller.email.toLowerCase(),
          role: 'seller',
          name: seller.displayName || clientForm.name.trim(),
        },
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      const invoiceRef = await addDoc(collection(db, 'clients', clientId, 'invoices'), payload);
      const freshSnap = await getDoc(invoiceRef);
      const savedData = freshSnap.data() || payload;

      setInvoices((prev) => [
        {
          id: invoiceRef.id,
          refPath: invoiceRef.path,
          ...savedData,
        },
        ...prev,
      ]);
      sendInvoiceEmailToClient({ ...savedData, id: invoiceRef.id }, { silent: true });
      setBuilderOpen(false);
      resetBuilder();
    } catch (err) {
      console.error('❌ Error creando factura desde vendedor:', err);
      setBuilderError(err.message || 'No pudimos crear la factura.');
    } finally {
      setSavingInvoice(false);
    }
  };

  const availableDesigns = useMemo(
    () => designCatalog.filter((design) => design.categoryId === catalogBuilder.categoryId),
    [designCatalog, catalogBuilder.categoryId]
  );

  useEffect(() => {
    if (!catalogBuilder.productId && availableDesigns.length) {
      setCatalogBuilder((prev) => ({ ...prev, productId: availableDesigns[0].id }));
    }
  }, [availableDesigns, catalogBuilder.productId]);

  const catalogPricePreview = useMemo(() => {
    const width = Number(catalogBuilder.width);
    const height = Number(catalogBuilder.height);
    if (!width || !height) return null;
    return getWindowBasePrice(width, height, catalogBuilder.series);
  }, [catalogBuilder.width, catalogBuilder.height, catalogBuilder.series]);

  const filteredAccessories = useMemo(() => {
    return accessoryOptions.filter(
      (addon) =>
        !addon.appliesTo?.length ||
        addon.appliesTo.includes(catalogBuilder.categoryId) ||
        addon.appliesTo.includes('todos')
    );
  }, [accessoryOptions, catalogBuilder.categoryId]);

  const handleAddonQtyChange = (id, rawValue) => {
    const value = Number(rawValue);
    setAddonSelection((prev) => {
      const next = { ...prev };
      if (!value) {
        delete next[id];
      } else {
        next[id] = value;
      }
      return next;
    });
  };

  const handleAddCatalogLine = () => {
    if (!catalogBuilder.productId) {
      setBuilderError('Selecciona un modelo del catálogo.');
      return;
    }
    const width = Number(catalogBuilder.width);
    const height = Number(catalogBuilder.height);
    const quantity = Number(catalogBuilder.quantity) || 1;
    const autoPriceInfo = width && height ? getWindowBasePrice(width, height, catalogBuilder.series) : null;
    const unitPrice = autoPriceInfo?.price || 0;
    const selectedDesign = designCatalog.find((design) => design.id === catalogBuilder.productId);
    const colorLabel = colorOptions.find((opt) => opt.id === catalogBuilder.color)?.label;
    const glassLabel = glassOptions.find((opt) => opt.id === catalogBuilder.glass)?.label;
    const finishLabel = finishOptions.find((opt) => opt.id === catalogBuilder.finish)?.label;

    const descriptionParts = [
      width && height ? `${width}" x ${height}"` : null,
      catalogBuilder.series ? `Serie ${catalogBuilder.series.toUpperCase()}` : null,
      colorLabel,
      glassLabel,
      finishLabel,
    ].filter(Boolean);

    if (autoPriceInfo?.widthExceeded) {
      descriptionParts.push('Ancho supera la tabla, validar durante visita.');
    }

    const baseItem = {
      name: `${selectedDesign?.title || 'Modelo'} · ${
        selectedDesign?.categoryName || productCategories.find((c) => c.id === catalogBuilder.categoryId)?.title || ''
      }`,
      description: descriptionParts.join(' · ') || 'Modelo del catálogo',
      quantity,
      price: unitPrice,
      catalogMeta: {
        productId: catalogBuilder.productId,
        categoryId: catalogBuilder.categoryId,
        width,
        height,
        series: catalogBuilder.series,
        color: catalogBuilder.color,
        glass: catalogBuilder.glass,
        finish: catalogBuilder.finish,
        autoPriceInfo,
      },
    };

    const entries = [baseItem];
    Object.entries(addonSelection).forEach(([accessoryId, qtyValue]) => {
      const qty = Number(qtyValue);
      if (!qty) return;
      const info = accessoryOptions.find((acc) => acc.id === accessoryId);
      if (!info) return;
      entries.push({
        name: `Accesorio · ${info.name}`,
        description: info.description || '',
        quantity: qty,
        price: Number(info.price) || 0,
        catalogMeta: { accessoryId },
      });
    });

    setItemList((prev) => [...prev, ...entries]);
    setCatalogBuilder((prev) => ({ ...prev, width: '', height: '', quantity: 1 }));
    setAddonSelection({});
    setBuilderError('');
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-gray-900">Facturas generadas</h2>
        <p className="text-gray-500">
          Seguimiento de facturas donde apareces como vendedor responsable.
        </p>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => {
            resetBuilder();
            setBuilderOpen(true);
          }}
          className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-emerald-700"
        >
          Crear factura
        </button>
        <p className="text-xs uppercase tracking-[0.3em] text-gray-400">
          {loading ? 'Cargando…' : `${invoices.length} facturas`}
        </p>
      </div>

      {error && (
        <div className="rounded border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      {loading ? (
        <p className="text-sm text-gray-500">Cargando facturas…</p>
      ) : invoices.length === 0 ? (
        <p className="text-sm text-gray-500">Aún no tienes facturas asignadas.</p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow">
          <table className="min-w-full divide-y divide-gray-100 text-sm">
            <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-4 py-3">Cliente</th>
                <th className="px-4 py-3">Total</th>
                <th className="px-4 py-3">Depósito</th>
                <th className="px-4 py-3">Saldo</th>
                <th className="px-4 py-3">Estado</th>
                <th className="px-4 py-3">Actualización</th>
                <th className="px-4 py-3 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {invoices.map((invoice) => (
                <tr key={invoice.refPath} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <p className="font-semibold text-gray-900">
                      {invoice.clientName || invoice.clientEmail || 'Cliente sin nombre'}
                    </p>
                    <p className="text-xs text-gray-500">{invoice.clientEmail}</p>
                  </td>
                  <td className="px-4 py-3">${Number(invoice.total || 0).toFixed(2)}</td>
                  <td className="px-4 py-3">${Number(invoice.deposit || 0).toFixed(2)}</td>
                  <td className="px-4 py-3 text-emerald-700">
                    ${Number(invoice.remaining || 0).toFixed(2)}
                  </td>
                  <td className="px-4 py-3 capitalize">{invoice.status || 'pendiente'}</td>
                  <td className="px-4 py-3 text-xs text-gray-500">
                    {invoice.updatedAt?.toDate?.().toLocaleDateString?.('es-PR') || '—'}
                  </td>
                  <td className="px-4 py-3 text-right text-xs">
                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          setPdfGeneratingId(invoice.id);
                          downloadInvoicePdf(invoice)
                            .catch((err) => console.error('❌ Error descargando factura:', err))
                            .finally(() => setPdfGeneratingId(''));
                        }}
                        className="rounded-full border border-gray-200 px-3 py-1 text-gray-600 hover:bg-gray-100 disabled:cursor-wait disabled:opacity-60"
                        disabled={pdfGeneratingId === invoice.id}
                      >
                        {pdfGeneratingId === invoice.id ? 'Descargando…' : 'Descargar'}
                      </button>
                      <button
                        type="button"
                        onClick={async () => {
                          setEmailSendingId(invoice.id);
                          try {
                            await sendInvoiceEmailToClient(invoice, { silent: false });
                          } finally {
                            setEmailSendingId('');
                          }
                        }}
                        className="rounded-full border border-emerald-200 px-3 py-1 text-emerald-700 hover:bg-emerald-50 disabled:cursor-wait disabled:opacity-60"
                        disabled={emailSendingId === invoice.id}
                      >
                        {emailSendingId === invoice.id ? 'Enviando…' : 'Enviar email'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {builderOpen && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/50 px-4">
          <div className="max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-3xl bg-white p-6 shadow-2xl">
            <div className="flex items-start justify-between gap-3 border-b border-gray-100 pb-4">
              <div>
                <p className="text-xs uppercase tracking-[0.35em] text-gray-400">Nueva factura</p>
                <h3 className="text-2xl font-semibold text-gray-900">Registra tu venta</h3>
                <p className="text-sm text-gray-500">
                  Asocia el proyecto a tu cliente y define los modelos o servicios incluidos.
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setBuilderOpen(false);
                  resetBuilder();
                }}
                className="text-sm font-semibold text-gray-400 hover:text-gray-700"
              >
                Cerrar
              </button>
            </div>

            <form className="mt-6 space-y-6" onSubmit={handleSubmitInvoice}>
              <div className="grid gap-3 md:grid-cols-2">
                <div>
                  <label className="text-xs font-semibold text-gray-600">Cliente existente</label>
                  <select
                    value={clientForm.clientId}
                    onChange={(e) => handleSelectClient(e.target.value)}
                    className="mt-1 w-full rounded-2xl border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:border-emerald-500 focus:outline-none"
                  >
                    <option value="">— Seleccionar —</option>
                    {clients.map((client) => (
                      <option key={client.id} value={client.id}>
                        {(client.businessName || client.name || 'Cliente sin nombre').slice(0, 40)}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-600">Estado</label>
                  <select
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    className="mt-1 w-full rounded-2xl border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:border-emerald-500 focus:outline-none"
                  >
                    {statusOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid gap-3 md:grid-cols-2">
                <div>
                  <label className="text-xs font-semibold text-gray-600">Nombre del cliente</label>
                  <input
                    type="text"
                    value={clientForm.name}
                    onChange={(e) => setClientForm((prev) => ({ ...prev, name: e.target.value }))}
                    className="mt-1 w-full rounded-2xl border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:border-emerald-500 focus:outline-none"
                    required
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-600">Correo</label>
                  <input
                    type="email"
                    value={clientForm.email}
                    onChange={(e) =>
                      setClientForm((prev) => ({ ...prev, email: e.target.value }))
                    }
                    className="mt-1 w-full rounded-2xl border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:border-emerald-500 focus:outline-none"
                    required
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-600">Teléfono</label>
                  <input
                    type="tel"
                    value={clientForm.phone}
                    onChange={(e) =>
                      setClientForm((prev) => ({ ...prev, phone: e.target.value }))
                    }
                    className="mt-1 w-full rounded-2xl border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:border-emerald-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-600">Ubicación / proyecto</label>
                  <input
                    type="text"
                    value={clientForm.projectLocation}
                    onChange={(e) =>
                      setClientForm((prev) => ({ ...prev, projectLocation: e.target.value }))
                    }
                    className="mt-1 w-full rounded-2xl border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:border-emerald-500 focus:outline-none"
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <label className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Modelos / servicios
                  </label>
                  <button
                    type="button"
                    onClick={addItem}
                    className="text-sm font-semibold text-emerald-600 hover:text-emerald-800"
                  >
                    + Añadir línea
                  </button>
                </div>
                <div className="mt-3 space-y-3">
                  {itemList.map((item, index) => (
                    <div
                      key={`invoice-item-${index}`}
                      className="rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3"
                    >
                      <div className="grid gap-2 md:grid-cols-2">
                        <div>
                          <label className="text-xs font-semibold text-gray-600">Descripción</label>
                          <input
                            type="text"
                            value={item.name}
                            onChange={(e) => handleItemChange(index, 'name', e.target.value)}
                            className="mt-1 w-full rounded-2xl border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:border-emerald-500 focus:outline-none"
                            placeholder="Ventana casement · Impact Series"
                            required={index === 0}
                          />
                        </div>
                        <div>
                          <label className="text-xs font-semibold text-gray-600">Notas</label>
                          <input
                            type="text"
                            value={item.description}
                            onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                            className="mt-1 w-full rounded-2xl border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:border-emerald-500 focus:outline-none"
                            placeholder={'72" x 48" · Lama 3 · Clear'}
                          />
                        </div>
                      </div>
                      <div className="mt-2 grid gap-2 md:grid-cols-3">
                        <div>
                          <label className="text-xs font-semibold text-gray-600">Cantidad</label>
                          <input
                            type="number"
                            min="1"
                            value={item.quantity}
                            onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                            className="mt-1 w-full rounded-2xl border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:border-emerald-500 focus:outline-none"
                          />
                        </div>
                        <div>
                          <label className="text-xs font-semibold text-gray-600">Precio unitario</label>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={item.price}
                            onChange={(e) => handleItemChange(index, 'price', e.target.value)}
                            className="mt-1 w-full rounded-2xl border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:border-emerald-500 focus:outline-none"
                          />
                        </div>
                        <div className="flex items-end justify-end">
                          {itemList.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeItem(index)}
                              className="text-xs font-semibold text-red-500 hover:text-red-700"
                            >
                              Eliminar
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="space-y-6 rounded-3xl border border-gray-200 bg-white/70 px-4 py-5">
                <div className="grid gap-3 sm:grid-cols-3">
                  <div>
                    <label className="text-xs font-semibold text-gray-600">Categoría</label>
                    <select
                      value={catalogBuilder.categoryId}
                      onChange={(e) =>
                        setCatalogBuilder((prev) => ({
                          ...prev,
                          categoryId: e.target.value,
                          productId:
                            designCatalog.find((design) => design.categoryId === e.target.value)?.id || '',
                        }))
                      }
                      className="mt-1 w-full rounded-2xl border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:border-emerald-500 focus:outline-none"
                    >
                      {productCategories.map((cat) => (
                        <option key={cat.id} value={cat.id}>
                          {cat.title}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="sm:col-span-2">
                    <label className="text-xs font-semibold text-gray-600">Modelo</label>
                    <select
                      value={catalogBuilder.productId}
                      onChange={(e) => setCatalogBuilder((prev) => ({ ...prev, productId: e.target.value }))}
                      className="mt-1 w-full rounded-2xl border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:border-emerald-500 focus:outline-none"
                    >
                      {availableDesigns.map((design) => (
                        <option key={design.id} value={design.id}>
                          {design.title} · {design.desc}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="grid gap-3 md:grid-cols-4">
                  <div>
                    <label className="text-xs font-semibold text-gray-600">Ancho (")</label>
                    <input
                      type="number"
                      min="1"
                      value={catalogBuilder.width}
                      onChange={(e) => setCatalogBuilder((prev) => ({ ...prev, width: e.target.value }))}
                      className="mt-1 w-full rounded-2xl border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:border-emerald-500 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-600">Alto (")</label>
                    <input
                      type="number"
                      min="1"
                      value={catalogBuilder.height}
                      onChange={(e) => setCatalogBuilder((prev) => ({ ...prev, height: e.target.value }))}
                      className="mt-1 w-full rounded-2xl border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:border-emerald-500 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-600">Serie</label>
                    <select
                      value={catalogBuilder.series}
                      onChange={(e) => setCatalogBuilder((prev) => ({ ...prev, series: e.target.value }))}
                      className="mt-1 w-full rounded-2xl border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:border-emerald-500 focus:outline-none"
                    >
                      <option value="lama3">Lama 3</option>
                      <option value="lama4">Lama 4</option>
                      <option value="lama6">Lama 6</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-600">Cantidad</label>
                    <input
                      type="number"
                      min="1"
                      value={catalogBuilder.quantity}
                      onChange={(e) => setCatalogBuilder((prev) => ({ ...prev, quantity: e.target.value }))}
                      className="mt-1 w-full rounded-2xl border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:border-emerald-500 focus:outline-none"
                    />
                  </div>
                </div>
                <div className="grid gap-3 md:grid-cols-3">
                  <div>
                    <label className="text-xs font-semibold text-gray-600">Color</label>
                    <select
                      value={catalogBuilder.color}
                      onChange={(e) => setCatalogBuilder((prev) => ({ ...prev, color: e.target.value }))}
                      className="mt-1 w-full rounded-2xl border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:border-emerald-500 focus:outline-none"
                    >
                      {colorOptions.map((color) => (
                        <option key={color.id} value={color.id}>
                          {color.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-600">Cristal</label>
                    <select
                      value={catalogBuilder.glass}
                      onChange={(e) => setCatalogBuilder((prev) => ({ ...prev, glass: e.target.value }))}
                      className="mt-1 w-full rounded-2xl border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:border-emerald-500 focus:outline-none"
                    >
                      {glassOptions.map((glass) => (
                        <option key={glass.id} value={glass.id}>
                          {glass.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-600">Acabado</label>
                    <select
                      value={catalogBuilder.finish}
                      onChange={(e) => setCatalogBuilder((prev) => ({ ...prev, finish: e.target.value }))}
                      className="mt-1 w-full rounded-2xl border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:border-emerald-500 focus:outline-none"
                    >
                      {finishOptions.map((finish) => (
                        <option key={finish.id} value={finish.id}>
                          {finish.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Accesorios del modelo
                  </p>
                  <div className="mt-2 grid gap-2 sm:grid-cols-2">
                    {filteredAccessories.map((addon) => (
                      <label
                        key={addon.id}
                        className="flex items-center justify-between rounded-2xl border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-600"
                      >
                        <span>
                          {addon.name}{' '}
                          <span className="text-[11px] text-gray-400">
                            (${Number(addon.price || 0).toFixed(2)})
                          </span>
                        </span>
                        <input
                          type="number"
                          min="0"
                          value={addonSelection[addon.id] || ''}
                          onChange={(e) => handleAddonQtyChange(addon.id, e.target.value)}
                          className="ml-2 w-16 rounded-xl border border-gray-200 px-2 py-1 text-right text-xs text-gray-700 focus:border-emerald-500 focus:outline-none"
                          placeholder="Qty"
                        />
                      </label>
                    ))}
                  </div>
                </div>
                {catalogPricePreview && (
                  <p className="rounded-2xl bg-emerald-50 px-4 py-2 text-sm text-emerald-700">
                    Precio sugerido: ${Number(catalogPricePreview.price || 0).toFixed(2)}{' '}
                    {catalogPricePreview.widthExceeded && (
                      <span className="text-orange-600">
                        · Ajustado al máximo de la tabla hasta confirmar medidas.
                      </span>
                    )}
                  </p>
                )}
                <button
                  type="button"
                  onClick={handleAddCatalogLine}
                  className="w-full rounded-2xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
                >
                  Agregar desde catálogo
                </button>
              </div>

              <div className="grid gap-3 md:grid-cols-3">
                <div>
                  <label className="text-xs font-semibold text-gray-600">Depósito (opcional)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={deposit}
                    onChange={(e) => setDeposit(e.target.value)}
                    className="mt-1 w-full rounded-2xl border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:border-emerald-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-600">Descuento</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={discount}
                    onChange={(e) => setDiscount(e.target.value)}
                    className="mt-1 w-full rounded-2xl border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:border-emerald-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-600">Notas</label>
                  <input
                    type="text"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    className="mt-1 w-full rounded-2xl border border-gray-200 px-3 py-2 text-sm text-gray-700 focus:border-emerald-500 focus:outline-none"
                    placeholder="Ej. Medidas por confirmar, incluye instalación."
                  />
                </div>
              </div>

              <p className="rounded-2xl bg-gray-50 px-4 py-3 text-sm font-semibold text-gray-900">
                Subtotal:{' '}
                <span className="text-emerald-700">
                  ${totals.subtotal.toLocaleString('es-PR', { minimumFractionDigits: 2 })}
                </span>{' '}
                · Saldo:{' '}
                <span className="text-emerald-700">
                  ${totals.remaining.toLocaleString('es-PR', { minimumFractionDigits: 2 })}
                </span>
              </p>

              {builderError && (
                <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
                  {builderError}
                </div>
              )}

              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-xs text-gray-500">
                  La factura se registrará en tu historial y notificará al admin del proyecto.
                </p>
                <button
                  type="submit"
                  disabled={savingInvoice}
                  className="rounded-full bg-emerald-600 px-5 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-emerald-400"
                >
                  {savingInvoice ? 'Guardando…' : 'Guardar factura'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SellerInvoices;
